import axios from 'axios';
import { appVersionUrls } from 'config/urls';

export const APP_CLIENT_VERSION_KEY = 'appClientVersion';
export const APP_VERSION_RELOAD_EVENT_KEY = 'appVersionReloadEvent';
const DEFAULT_CLIENT_VERSION = 1;

export type CurrentVersionResponse = {
    currentVersion?: number;
    updatedBy?: string;
    updatedAt?: string; // ISO timestamp e.g. "2026-03-04 07:42:42.339677+00:00"
};

type VersionCheckResponse = {
    refreshRequired?: boolean;
    currentVersion?: number;
};

export const getStoredClientVersion = () => {
    const stored = Number(localStorage.getItem(APP_CLIENT_VERSION_KEY));
    if (Number.isFinite(stored) && stored > 0) {
        return stored;
    }
    return DEFAULT_CLIENT_VERSION;
};

export const setStoredClientVersion = (version: number) => {
    if (!Number.isFinite(version) || version <= 0) return;
    localStorage.setItem(APP_CLIENT_VERSION_KEY, String(version));
};

/**
 * Fetches the latest app version from the backend (no payload).
 * Use after login/refresh to set this tab's client version. Does not trigger refresh logic.
 */
export const syncClientVersionFromApi = async (accessToken?: string) => {
    const headers: HeadersInit = {
        Accept: 'application/json',
        'Cache-Control': 'no-store',
    };

    if (accessToken) {
        (headers as Record<string, string>).Authorization = `Bearer ${accessToken}`;
    }

    const response = await fetch(appVersionUrls.getCurrentVersion, {
        cache: 'no-store',
        headers,
    });
    if (!response.ok) {
        return null;
    }

    const data: CurrentVersionResponse = await response.json();
    if (typeof data?.currentVersion === 'number' && data.currentVersion > 0) {
        setStoredClientVersion(data.currentVersion);
        return data.currentVersion;
    }

    return null;
};

/**
 * Fetches current version info (currentVersion, updatedBy, updatedAt). Use e.g. on Force Refresh page for Last Bumped.
 */
export const fetchCurrentVersionInfo = async (accessToken?: string): Promise<CurrentVersionResponse | null> => {
    const headers: HeadersInit = {
        Accept: 'application/json',
        'Cache-Control': 'no-store',
    };

    if (accessToken) {
        (headers as Record<string, string>).Authorization = `Bearer ${accessToken}`;
    }

    const response = await fetch(appVersionUrls.getCurrentVersion, {
        cache: 'no-store',
        headers,
    });
    if (!response.ok) return null;

    const data: CurrentVersionResponse = await response.json();
    return data ?? null;
};

/**
 * Polls version-check using this tab's stored client version and returns refresh decision.
 * Does not mutate stored client version; caller should reload when refreshRequired is true.
 * Uses axios so the shared request/response interceptors (token refresh + 401 handling) apply.
 */
export const checkIfRefreshRequired = async (_accessToken?: string) => {
    const clientVersion = getStoredClientVersion();
    try {
        const { data } = await axios.get<VersionCheckResponse>(appVersionUrls.versionCheck(clientVersion), {
            headers: {
                Accept: 'application/json',
                'Cache-Control': 'no-store',
            },
        });
        const currentVersion = typeof data?.currentVersion === 'number' ? data.currentVersion : null;
        const refreshRequired =
            data?.refreshRequired === true || (currentVersion !== null ? currentVersion > clientVersion : false);

        return {
            refreshRequired,
            currentVersion,
            clientVersion,
        };
    } catch {
        return null;
    }
};
